// Desktop Chromium (Windows/Linux) reports HEVC as supported through
// MediaSource.isTypeSupported even with no hardware decoder, so hls.js keeps the
// HEVC rungs, the ABR climbs into one, and software decode stutters on every
// ~2s CMAF segment. MediaCapabilities.decodingInfo tells them apart: it reports
// `powerEfficient` (hardware) vs software fallback. When HEVC is not smoothly
// hardware-decodable, we drop those rungs so the ABR stays on the H.264 ones
// the v8 ladder always carries.

import type { StartTarget } from './startTarget';

import {
	coversTarget,
	levelShortSide,
	startTarget,
	UNKNOWN_BANDWIDTH_START_SIDE
} from './startTarget';

export interface CodecLevel {
	videoCodec?: string;
	width?: number;
	height?: number;
	bitrate?: number;
}

export type DecodingInfoFn = (
	config: MediaDecodingConfiguration
) => Promise<MediaCapabilitiesDecodingInfo>;

/**
 * Apple WebKit: every iOS browser and in-app webview (Instagram included), iPadOS
 * in desktop mode, and Safari on macOS. These devices decode HEVC in hardware,
 * but decodingInfo for media-source answers as if they could not,
 * so the HEVC rungs were stripped. Chrome and Firefox on macOS answer
 * correctly and keep the check.
 */
export function isAppleWebKit(userAgent: string, maxTouchPoints = 0): boolean {
	if (/iPhone|iPad|iPod/.test(userAgent)) return true;
	const isMac = /Macintosh/.test(userAgent);
	if (isMac && maxTouchPoints > 1) return true;
	return (
		isMac &&
		/AppleWebKit/.test(userAgent) &&
		!/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR/.test(userAgent)
	);
}

// The answer depends on the device, not on the video: the same codec and size
// answer the same all session, so each video swap does not wait on it again.
// The bitrate stays out of the key: it moves a few kbps between videos of the
// same ladder and does not change what the decoder can take.
const decodingInfoMemo = new Map<
	string,
	Promise<MediaCapabilitiesDecodingInfo>
>();
// Some Android WebViews never settle decodingInfo (shaka-player#7680). Playback
// waits on it before startLoad, so past this it counts as a rejection.
export const DECODING_INFO_TIMEOUT_MS = 1_000;
// A timeout is kept this long as "unknown" so the next videos don't each wait
// the timeout again; a late real answer replaces it.
export const DECODING_INFO_UNKNOWN_TTL_MS = 60_000;

export function memoizeDecodingInfo(
	decodingInfo: DecodingInfoFn
): DecodingInfoFn {
	return config => {
		const video = config.video;
		const key = `${config.type}|${video?.contentType}|${video?.width}x${video?.height}`;
		let answer = decodingInfoMemo.get(key);
		if (!answer) {
			let timer: ReturnType<typeof setTimeout> | undefined;
			const real = decodingInfo(config);
			const timedOut = new Error('decodingInfo timed out');
			const pending = Promise.race([
				real,
				new Promise<never>((_, reject) => {
					timer = setTimeout(() => reject(timedOut), DECODING_INFO_TIMEOUT_MS);
				})
			]).finally(() => clearTimeout(timer));
			answer = pending;
			decodingInfoMemo.set(key, pending);
			const forget = () => {
				if (decodingInfoMemo.get(key) === pending) decodingInfoMemo.delete(key);
			};
			// A rejection is uncertainty, not an answer: the next video asks again.
			pending.catch(error => {
				if (error !== timedOut) {
					forget();
					return;
				}
				setTimeout(forget, DECODING_INFO_UNKNOWN_TTL_MS);
				real.then(
					info => {
						if (decodingInfoMemo.get(key) === pending) {
							decodingInfoMemo.set(key, Promise.resolve(info));
						}
					},
					() => {}
				);
			});
		}
		return answer;
	};
}

/**
 * The decodingInfo the HEVC gating should trust, or null to skip it (keep every
 * rung, treat hardware as available), which is what Apple WebKit needs.
 */
export function hevcDecodingInfo(): DecodingInfoFn | null {
	if (typeof navigator === 'undefined') return null;
	if (isAppleWebKit(navigator.userAgent, navigator.maxTouchPoints)) return null;
	const capabilities = navigator.mediaCapabilities;
	return capabilities?.decodingInfo
		? memoizeDecodingInfo(capabilities.decodingInfo.bind(capabilities))
		: null;
}

const HEVC_CODEC = /^(hvc1|hev1)\b/i;

export function isHevcLevel(level: CodecLevel): boolean {
	return !!level.videoCodec && HEVC_CODEC.test(level.videoCodec);
}

// Lambda masters declare hvc1…L150 (level 5) on every HEVC rung, portrait
// 720p/1080p included, while the bitstream is L90–L120. Judged by the declared
// string alone, a decoder that tops out at level 4.x loses rungs it can play.
// Max luma picture size per level, H.265 Table A.8.
const HEVC_LEVEL_BY_SIZE: ReadonlyArray<[maxLumaPs: number, level: number]> = [
	[552_960, 90],
	[983_040, 93],
	[2_228_224, 120]
];
const HEVC_CODEC_LEVEL = /^((?:hvc1|hev1)\.[^.]+\.[^.]+\.)([LH])(\d+)(.*)$/i;

/**
 * The codec string with its level lowered to the one the rung's size needs, or
 * null when the declared level is not above it or the size is unknown. Stops at
 * 1080p: past it, level 5 is what the rung really needs.
 */
export function hevcCodecForSize(
	codec: string,
	width?: number,
	height?: number
): string | null {
	const match = HEVC_CODEC_LEVEL.exec(codec);
	if (!match || !width || !height) return null;
	const [, prefix, tier, declared, rest] = match;
	let needed = HEVC_LEVEL_BY_SIZE.find(([max]) => width * height <= max)?.[1];
	if (!needed) return null;
	// High tier only exists from level 4 up.
	if (tier!.toUpperCase() === 'H') needed = Math.max(needed, 120);
	if (Number(declared) <= needed) return null;
	return `${prefix}${tier}${needed}${rest}`;
}

const typeSupportMemo = new Map<string, boolean>();

/**
 * MediaSource.isTypeSupported the way hls.js asks it, memoized per session.
 * True without MSE (native HLS, SSR): nothing filters a rung there.
 */
export function isVideoCodecTypeSupported(codec: string): boolean {
	let supported = typeSupportMemo.get(codec);
	if (supported === undefined) {
		const source =
			(globalThis as { ManagedMediaSource?: typeof MediaSource })
				.ManagedMediaSource ?? globalThis.MediaSource;
		supported = source
			? source.isTypeSupported(`video/mp4;codecs=${codec}`)
			: true;
		typeSupportMemo.set(codec, supported);
	}
	return supported;
}

/** The size-lowered codec when only it passes isSupported; else the declared one. */
export function relaxHevcCodec(
	codec: string,
	width: number | undefined,
	height: number | undefined,
	isSupported: (codec: string) => boolean
): string {
	const lower = hevcCodecForSize(codec, width, height);
	return lower && !isSupported(codec) && isSupported(lower) ? lower : codec;
}

/**
 * Lowers the overstated HEVC level of master rungs whose declared codec fails
 * isTypeSupported while the level their size needs passes. hls.js drops a rung
 * at parse on that check alone, before the gating below ever sees it; the
 * SourceBuffer still takes the codec parsed from the init segment.
 */
export function relaxOverstatedHevcLevels(
	playlist: string,
	isSupported: (codec: string) => boolean
): string {
	if (!playlist.includes('#EXT-X-STREAM-INF')) return playlist;
	return playlist.replace(/^#EXT-X-STREAM-INF:.*$/gm, line => {
		const size = /RESOLUTION=(\d+)x(\d+)/.exec(line);
		const width = Number(size?.[1]);
		const height = Number(size?.[2]);
		return line.replace(/CODECS="([^"]*)"/, (_attr, codecs: string) => {
			const relaxed = codecs
				.split(',')
				.map(codec => relaxHevcCodec(codec.trim(), width, height, isSupported));
			return `CODECS="${relaxed.join(',')}"`;
		});
	});
}

/**
 * Indices of the HEVC levels the device cannot decode smoothly in hardware, so
 * the caller can remove them. Guarantees:
 * - H.264 levels are never touched.
 * - Nothing is removed when the API is missing or throws (no regression on
 *   uncertainty — a working HEVC path must not be cut on a maybe).
 * - Never removes every level (a stuttering HEVC still beats a dead player), and
 *   only acts when at least one non-HEVC rung remains to fall back to.
 */
export async function hevcLevelsToRemove(
	levels: ReadonlyArray<CodecLevel>,
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<number[]> {
	if (!decodingInfo) return [];
	const hevc = levels
		.map((level, index) => ({ level, index }))
		.filter(entry => isHevcLevel(entry.level));
	if (hevc.length === 0) return [];
	// No H.264 rung to fall back to — leave the ladder alone.
	if (levels.length - hevc.length === 0) return [];

	// All rungs probed at once: the window between MANIFEST_PARSED and the
	// removal is exactly when a video switch can invalidate the result, so it
	// should be one round trip, not one per rung.
	const infos = await Promise.all(
		hevc.map(
			async ({ level }): Promise<MediaCapabilitiesDecodingInfo | undefined> => {
				const probe = (videoCodec: string) =>
					decodingInfo({
						type: 'media-source',
						video: {
							contentType: `video/mp4; codecs="${videoCodec}"`,
							width: level.width ?? 1920,
							height: level.height ?? 1080,
							bitrate: level.bitrate ?? 2_000_000,
							framerate: 30
						}
					});
				try {
					const declared = await probe(level.videoCodec!);
					const lower = hevcCodecForSize(
						level.videoCodec!,
						level.width,
						level.height
					);
					if (decodesInHardware(declared) || !lower) return declared;
					// Overstated level (L150 on a 1080p rung): judge the rung by
					// the level its size needs, which is what the decoder gets.
					return await probe(lower);
				} catch {
					// Fail-open on uncertainty: API missing or throwing shouldn't break HEVC
					return undefined;
				}
			}
		)
	);
	const remove: number[] = [];
	hevc.forEach(({ index }, i) => {
		const info = infos[i];
		// Keep on uncertainty; remove only when the platform confirms it can't
		// decode this rung smoothly in hardware.
		if (info && !decodesInHardware(info)) {
			remove.push(index);
		}
	});

	if (remove.length >= levels.length) return [];
	return remove;
}

function decodesInHardware(info: MediaCapabilitiesDecodingInfo): boolean {
	return info.supported && info.smooth && info.powerEfficient;
}

/** What survives a ladder mutation: hls.js never rewrites these on a rung. */
export function levelIdentity(level: CodecLevel): string {
	return `${level.videoCodec ?? ''}|${level.height ?? ''}|${level.bitrate ?? ''}`;
}

/**
 * Maps indices computed against a pre-await snapshot onto the live ladder.
 * hls.js can drop a rung on its own within the same load (codec or key error),
 * so a snapshot index may now name a different rung. Targets are matched by
 * identity; ones already gone are skipped; and the result is never the whole
 * live ladder, so a caller cannot strip everything whatever moved underneath.
 */
export function resolveLevelIndices(
	snapshot: ReadonlyArray<CodecLevel>,
	indices: ReadonlyArray<number>,
	live: ReadonlyArray<CodecLevel>
): number[] {
	const targets = new Set(
		indices.flatMap(i => {
			const level = snapshot[i];
			return level ? [levelIdentity(level)] : [];
		})
	);
	const resolved = live
		.map((level, i) => (targets.has(levelIdentity(level)) ? i : -1))
		.filter(i => i >= 0);
	return resolved.length >= live.length ? [] : resolved;
}

/**
 * Returns whether the ladder contains HEVC rungs, indicating hardware HEVC
 * support survived the gating phase.
 */
export function hasHevcLevels(levels: ReadonlyArray<CodecLevel>): boolean {
	return levels.some(level => isHevcLevel(level));
}

/**
 * Every HEVC rung, for when the device already failed to decode HEVC. Empty when
 * no H.264 rung would remain: a ladder with nothing left to play is worse than a
 * rung that might still decode.
 */
export function hevcFallbackIndices(
	levels: ReadonlyArray<CodecLevel>
): number[] {
	const hevc = levels
		.map((level, index) => (isHevcLevel(level) ? index : -1))
		.filter(index => index >= 0);
	if (hevc.length === 0 || hevc.length === levels.length) return [];
	return hevc;
}

export interface DecodeProgress {
	readyState: number;
	/** Seconds buffered ahead of currentTime. */
	bufferedAhead: number;
	/** Frames decoded so far; null when getVideoPlaybackQuality is missing. */
	decodedFrames: number | null;
}

// HTMLMediaElement.HAVE_CURRENT_DATA, spelled out so this stays usable in tests.
const HAVE_CURRENT_DATA = 2;

/**
 * The hang that raises no error: segments arrived but not a single frame came out.
 * Some Android decoders (Motorola, Samsung, in-app webviews) accept HEVC on
 * MediaCapabilities and then produce nothing, so the viewer waits and leaves.
 */
export function isDecodeStall(progress: DecodeProgress): boolean {
	return (
		progress.readyState < HAVE_CURRENT_DATA &&
		progress.bufferedAhead >= 1 &&
		(progress.decodedFrames ?? 0) === 0
	);
}

interface BufferedRanges {
	readonly length: number;
	start(index: number): number;
	end(index: number): number;
}

/** Seconds buffered ahead of `currentTime` in the range that contains it. */
export function bufferedAhead(
	ranges: BufferedRanges,
	currentTime: number
): number {
	for (let i = 0; i < ranges.length; i++) {
		if (ranges.start(i) <= currentTime && currentTime <= ranges.end(i)) {
			return ranges.end(i) - currentTime;
		}
	}
	return 0;
}

/**
 * Frames decoded since `baseline`. The counter belongs to the element, which the
 * feed reuses across videos; a counter below the baseline was reset by the browser.
 */
export function framesDecodedSince(
	total: number | null,
	baseline: number
): number | null {
	if (total === null) return null;
	return total >= baseline ? total - baseline : total;
}

/**
 * The rung a play starts on. The player pins it and the warm chain downloads
 * it, so both must pick the same one: the smallest HEVC rung when the device
 * takes HEVC in hardware, else the smallest non-HEVC one; ties go to the lower
 * bitrate. With a target, the rung that covers the element (capLevelToPlayerSize's
 * rule), lowered to the largest the bandwidth budget pays for; with no budget
 * known, to UNKNOWN_BANDWIDTH_START_SIDE. -1 on an empty ladder.
 */
export function pickStartLevel(
	levels: ReadonlyArray<CodecLevel>,
	hevcOk: boolean,
	target: StartTarget | null = null
): number {
	const entries = levels.map((level, index) => ({ level, index }));
	const hevc = entries.filter(entry => isHevcLevel(entry.level));
	const others = entries.filter(entry => !isHevcLevel(entry.level));
	const pool =
		hevcOk && hevc.length > 0 ? hevc : others.length > 0 ? others : entries;
	pool.sort(
		(a, b) =>
			(a.level.height ?? 0) - (b.level.height ?? 0) ||
			(a.level.bitrate ?? 0) - (b.level.bitrate ?? 0)
	);
	if (!target) return pool[0]?.index ?? -1;
	const coverAt = pool.findIndex(entry => coversTarget(entry.level, target));
	const cover = coverAt === -1 ? pool.length - 1 : coverAt;
	const budget = target.budgetBps;
	let fit = 0;
	pool.forEach((entry, i) => {
		const fits =
			budget === null
				? levelShortSide(entry.level) <= UNKNOWN_BANDWIDTH_START_SIDE
				: (entry.level.bitrate ?? Infinity) <= budget;
		if (fits) fit = i;
	});
	return pool[Math.min(cover, fit)]?.index ?? -1;
}

/**
 * autoLevelCapping that keeps the ABR at the rung covering the element, the
 * way hls.js capLevelToPlayerSize does (which would also overwrite the
 * bandwidth cap, hence computed here). -1 when there is nothing to cap.
 */
export function sizeCapIndex(
	levels: ReadonlyArray<CodecLevel>,
	target: StartTarget | null
): number {
	if (!target || !levels.length) return -1;
	const sides = levels.map(levelShortSide);
	const covering = sides.filter((_, i) => coversTarget(levels[i]!, target));
	const limit = covering.length ? Math.min(...covering) : Math.max(...sides);
	// The cap is an index into a ladder hls.js sorts by bitrate, and rungs of
	// different sizes can declare the same one: stop at the first larger rung.
	let cap = -1;
	while (cap + 1 < sides.length && sides[cap + 1]! <= limit) cap++;
	return Math.max(cap, 0);
}

/**
 * Rungs outside the codec family the playback starts on (pickStartLevel's).
 * HEVC and H.264 segment boundaries do not line up in the mixed ladders, so an
 * ABR switch across codecs glitches: a playback stays in one family. Never
 * the whole ladder, the start rung always stays.
 */
export function codecLockIndices(
	levels: ReadonlyArray<CodecLevel>,
	hevcOk: boolean
): number[] {
	const start = levels[pickStartLevel(levels, hevcOk)];
	if (!start) return [];
	const lockToHevc = isHevcLevel(start);
	return levels
		.map((level, index) => (isHevcLevel(level) === lockToHevc ? -1 : index))
		.filter(index => index >= 0);
}

/**
 * pickStartLevel over the ladder the player will have after MANIFEST_PARSED
 * (oversized rungs, HEVC after a session failure, HEVC the device cannot take
 * in hardware all removed). For the warm chain, which runs without hls.js;
 * the answers are memoized, so the player then gets them for free.
 */
export async function predictStartLevel(
	levels: ReadonlyArray<CodecLevel>,
	target: StartTarget | null = startTarget()
): Promise<number> {
	const decodingInfo = hevcDecodingInfo();
	const failed = hevcDecodeFailedThisSession();
	let pool = levels.map((level, index) => ({ level, index }));
	const drop = (indices: number[]) => {
		const gone = new Set(indices);
		pool = pool.filter((_, i) => !gone.has(i));
	};
	drop(oversizedLevelIndices(pool.map(entry => entry.level)));
	if (failed) drop(hevcFallbackIndices(pool.map(entry => entry.level)));
	drop(
		await hevcLevelsToRemove(
			pool.map(entry => entry.level),
			decodingInfo
		)
	);
	const hevcOk = !failed && (await canHardwareDecodeHevc(decodingInfo));
	const start = pickStartLevel(
		pool.map(entry => entry.level),
		hevcOk,
		target
	);
	return pool[start]?.index ?? -1;
}

// The phone screen gains nothing past 1080p, and the 2160x3840 HEVC rung of the
// 29/09 re-encode is the one a phone decoder is most likely to refuse.
const MAX_SHORT_SIDE = 1080;

/** Rungs larger than 1080p on the short side; never the whole ladder. */
export function oversizedLevelIndices(
	levels: ReadonlyArray<CodecLevel>,
	maxShortSide = MAX_SHORT_SIDE
): number[] {
	const oversized = levels
		.map((level, index) =>
			level.width &&
			level.height &&
			Math.min(level.width, level.height) > maxShortSide
				? index
				: -1
		)
		.filter(index => index >= 0);
	return oversized.length >= levels.length ? [] : oversized;
}

export type HevcFallbackReason = 'media_error' | 'element_decode' | 'stall';

// A device that failed HEVC once fails it on every video of the session (48% of
// same-format plays after a failure on Android in Sep/2026), so the fallback sticks.
// A decode error is the device saying no; a stall only suggests it, so it has to
// happen twice before the session gives up on HEVC. Kept across visits for a
// week: the decoder is the device's, so the next visit would fail the same way.
const HEVC_FAILED_KEY = 'pilar:hevc-decode-failed-at';
const HEVC_FAILED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const STALLS_BEFORE_STICKY = 2;
let hevcFailedInMemory = false;
let storageRead = false;
let stallCount = 0;

// hls.js media errors that mean the decoder or SourceBuffer refused the codec;
// a parsing error is a bad segment, not the device.
const DECODER_ERROR_DETAILS = new Set([
	'bufferAddCodecError',
	'bufferIncompatibleCodecsError',
	'bufferAppendError'
]);

export function markHevcDecodeFailed(
	reason: HevcFallbackReason,
	detail?: string
): void {
	if (reason === 'stall' && ++stallCount < STALLS_BEFORE_STICKY) return;
	hevcFailedInMemory = true;
	storageRead = true;
	// Only the decoder saying no outlives the page: a stall can be the network
	// and other media errors a bad segment.
	const decoderFailed =
		reason === 'element_decode' ||
		(reason === 'media_error' && DECODER_ERROR_DETAILS.has(detail ?? ''));
	if (!decoderFailed) return;
	try {
		localStorage.setItem(HEVC_FAILED_KEY, String(Date.now()));
	} catch {
		// Private mode or SSR: the in-memory flag still covers this page's lifetime.
	}
}

// Called on every canvas frame, so storage is read once and memory answers after.
export function hevcDecodeFailedThisSession(): boolean {
	if (!storageRead) {
		storageRead = true;
		try {
			const failedAt = Number(localStorage.getItem(HEVC_FAILED_KEY));
			hevcFailedInMemory ||=
				failedAt > 0 && Date.now() - failedAt < HEVC_FAILED_TTL_MS;
		} catch {
			// Storage unavailable: nothing was remembered.
		}
	}
	return hevcFailedInMemory;
}

/** Test hook: forgets the remembered failure. */
export function resetHevcDecodeFailure(): void {
	hevcFailedInMemory = false;
	storageRead = false;
	stallCount = 0;
	try {
		localStorage.removeItem(HEVC_FAILED_KEY);
	} catch {
		// Storage unavailable.
	}
}

/**
 * Pre-checks if the device can hardware-decode a representative HEVC config
 * (1080p30 2mbps hvc1) before the Hls instance is created. This determines
 * whether to set `videoPreference: { videoCodec: 'hvc1' }` in the constructor,
 * guiding initial selection toward HEVC per hls.js v1.7.0 API.md:
 * "videoPreference limits initial level selection when a baseline variant exists."
 *
 * Returns true (HW-OK) when MediaCapabilities confirms supported+smooth+powerEfficient,
 * false otherwise. Fail-open on API absence or throw: uncertainty should not break
 * an otherwise working HEVC path, so the caller may still attempt HEVC and rely on
 * the post-parse removeLevel gating to strip unplayable rungs.
 *
 * @see https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
 */
export async function canHardwareDecodeHevc(
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<boolean> {
	if (!decodingInfo) return true;
	try {
		const info = await decodingInfo({
			type: 'media-source',
			video: {
				contentType: 'video/mp4; codecs="hvc1.1.6.L120.90"',
				width: 1920,
				height: 1080,
				bitrate: 2_000_000,
				framerate: 30
			}
		});
		return info.supported && info.smooth && info.powerEfficient;
	} catch {
		return true;
	}
}
